import { events } from "@/common/constants";
import { misc, theme } from "@/common/theme";
import ShuffledText from "@/mono/components/ShuffledText";
import { wait } from "@/mono/lib/common";
import emitter from "@/mono/lib/emitter";
import useAnimation, {
  useToggleEventAnimation,
} from "@/mono/lib/hooks/useAnimation";
import useEvent from "@/mono/lib/hooks/useEvent";
import { mouseState } from "@/mono/lib/hooks/useMouseMove";
import useRafLoop, { useTicker } from "@/mono/lib/hooks/useRaf";
import { damp, lerp, map } from "@/mono/lib/math";
import { EASE_CUSTOM_2, EASE_CUSTOM_4 } from "@/mono/lib/styles/easing";
import { useGlobalStore } from "@/mono/store/global";
import { damp2 } from "maath/easing";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Vector2 } from "three";

const size = 30;

const { CURSOR_HIDE, CURSOR_SHOW, CURSOR_POINTER } = events;

// export const CURSOR_LERP = 0.15;
export const CURSOR_LERP = 0.1;

export function Cursor() {
  const _pos = useMemo(() => new Vector2(), []);
  const wrapper = useRef();
  const cursor = useRef();

  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);

  const visible = useRef(false);

  const shuffledText = useRef();

  const [value, _value] = [useRef(0), useRef(0)];

  const [width, setWidth] = useState(0);

  const { start, stop } = useTicker(
    ({ delta }) => {
      // TODO: DAMP?
      // TODO: Hide when out of focus, see nose proj
      // _pos.lerp(mouseState.getState().mouse, CURSOR_LERP);

      const { x, y } = mouseState.getState().mouse;

      damp2(
        _pos,
        [x, y],
        0.1,
        0.01,
        Infinity
        // (t) => 1 / (1 + t + 0.48 * t * t + 0.235 * t * t * t),
        // 0.001
      );

      _value.current = lerp(_value.current, value.current, 0.7, delta);

      // TEMP:
      const deg = 0;
      let rotation = map(_value.current, 0, 1, -deg, 0);
      if (!visible.current) rotation = map(_value.current, 0, 1, deg, 0);

      if (cursor.current)
        cursor.current.style.transform = `scale(${_value.current}) rotate(${rotation}deg)`;

      if (wrapper.current)
        wrapper.current.style.transform = `translate(${_pos.x}px, ${_pos.y}px)`;
    },
    { initiallyActive: false }
  );

  const { animateIn, animateOut } = useAnimation({
    inParams: {
      ease: EASE_CUSTOM_4,
      duration: 0.4,
      onStart: () => {
        visible.current = true;
        start();

        wrapper.current.style.display = "block";
        shuffledText.current?.animateIn?.({ duration: 0.2 });
      },
      onUpdate: (v) => {
        value.current = v;
        //cursor.current.style.transform = `scale(${v})`;
      },
    },
    outParams: {
      ease: EASE_CUSTOM_4,
      duration: 0.2,
      onStart: () => {
        visible.current = false;
        shuffledText.current?.animateOut?.({ duration: 0.2 });
      },
      onUpdate: (v) => {
        value.current = v;
        //cursor.current.style.transform = `scale(${v})`;
      },
      onComplete: async () => {
        if (wrapper.current) wrapper.current.style.display = "none";

        //await wait(1000);
        stop();
        //shuffledText.current.setText("");
      },
    },
  });

  useEvent(CURSOR_SHOW, async ({ text } = {}) => {
    if (visible.current) return;
    const width = await shuffledText.current.setText(text);
    const padding = 40;
    if (width > 0 && wrapper.current)
      wrapper.current.style.width = `${width + padding * 2}px`;

    await wait(100);
    animateIn();
  });

  useEvent(CURSOR_HIDE, () => {
    if (!visible.current) return;
    animateOut();
  });

  if (isMobileLayout) return null;

  return (
    <Wrapper ref={wrapper}>
      <div className="cursor-inner" ref={cursor}>
        <StyledCursor></StyledCursor>
        <ShuffledText immediate={false} ref={shuffledText}></ShuffledText>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: block;
  position: fixed;
  top: ${size}px;
  left: ${size}px;
  min-width: ${size}px;
  height: ${size}px;
  display: block;
  z-index: 10000;

  padding: 20rem 40rem;

  pointer-events: none;

  .cursor-inner {
    position: relative;

    height: 100%;

    border: 1px solid ${({ theme }) => theme.theme.textColor};
    transform: translate(-50%, -50%) scale(0);
    border-radius: 5px;

    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0rem 60rem;
  }

  .txt-shuffle-wrapper {
    z-index: 9;
    position: absolute;
    top: 50%;

    transform: translateY(-50%);

    .txt-shuffle {
      color: ${({ theme }) => theme.theme.background} !important;
      font-weight: 900;
      white-space: nowrap;
      text-align: center;
    }
  }
`;

const StyledCursor = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  background-color: ${({ theme }) => theme.theme.textColor};
`;
